/**
 * IVR Configuration - Redesigned
 * Complete Intent IVR setup with call flow, providers, and intents
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Settings, Plus, Edit2, Trash2, Save,
  MessageSquare, Phone, GitBranch, Zap, Volume2, ArrowLeft, RefreshCw,
  PhoneIncoming, PhoneOff, HelpCircle, ArrowRight, Mic, Brain, X, ClipboardList
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

// Provider configurations with models/voices
const STT_PROVIDERS = {
  soniox: { name: 'Soniox', models: [
    { value: 'stt-rt-preview', label: 'Real-time Preview' },
    { value: 'stt-async-preview', label: 'Async Preview' }
  ]},
  deepgram: { name: 'Deepgram', models: [
    { value: 'nova-2', label: 'Nova 2 (Latest)' },
    { value: 'nova', label: 'Nova' },
    { value: 'enhanced', label: 'Enhanced' }
  ]},
  google: { name: 'Google', models: [
    { value: 'latest_long', label: 'Latest Long' },
    { value: 'telephony', label: 'Telephony' }
  ]}
};

// Offline fallback only. The live list comes from GET /api/agents/llm-models
// (source of truth: api/src/config/llm-catalog.js) — see buildClassifierProviders.
//
// Anthropic used to be listed here but the bridge only builds Groq/OpenAI base
// URLs for the classifier, so selecting it produced a request to OpenAI with a
// Claude model ID and a guaranteed failure. Removed rather than left as a trap.
const FALLBACK_CLASSIFIER_PROVIDERS = {
  groq: { name: 'Groq (Fast)', models: [
    { value: 'openai/gpt-oss-20b', label: 'GPT-OSS 20B (Fastest)' },
    { value: 'openai/gpt-oss-120b', label: 'GPT-OSS 120B' }
  ]},
  openai: { name: 'OpenAI', models: [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'gpt-4o', label: 'GPT-4o' }
  ]}
};

const CLASSIFIER_PROVIDER_NAMES = { groq: 'Groq (Fast)', openai: 'OpenAI' };

/** Reshape the API catalog into the {provider: {name, models[]}} shape used below. */
function buildClassifierProviders(models) {
  const byProvider = {};
  for (const m of models) {
    // Ollama has no hosted classifier path in the bridge, so it is not offered here.
    if (!CLASSIFIER_PROVIDER_NAMES[m.provider]) continue;
    if (!byProvider[m.provider]) {
      byProvider[m.provider] = { name: CLASSIFIER_PROVIDER_NAMES[m.provider], models: [] };
    }
    const suffix = m.status === 'preview' ? ' (Preview)' : m.status === 'legacy' ? ' (Legacy)' : '';
    byProvider[m.provider].models.push({ value: m.id, label: `${m.name}${suffix}` });
  }
  return Object.keys(byProvider).length ? byProvider : FALLBACK_CLASSIFIER_PROVIDERS;
}

const TTS_PROVIDERS = {
  elevenlabs: { name: 'ElevenLabs', voices: [
    { value: 'Rachel', label: 'Rachel (Female)' },
    { value: 'Domi', label: 'Domi (Female)' },
    { value: 'Bella', label: 'Bella (Female)' },
    { value: 'Sarah', label: 'Sarah (Female)' },
    { value: 'Nicole', label: 'Nicole (Female)' },
    { value: 'Charlotte', label: 'Charlotte (Swedish Female)' },
    { value: 'Antoni', label: 'Antoni (Male)' },
    { value: 'Josh', label: 'Josh (Male)' },
    { value: 'Arnold', label: 'Arnold (Male)' },
    { value: 'Adam', label: 'Adam (Male)' },
    { value: 'Sam', label: 'Sam (Male)' },
    { value: 'Daniel', label: 'Daniel (British Male)' }
  ]},
  deepgram: { name: 'Deepgram Aura', voices: [
    { value: 'aura-asteria-en', label: 'Asteria (Female)' },
    { value: 'aura-luna-en', label: 'Luna (Female)' },
    { value: 'aura-stella-en', label: 'Stella (Female)' },
    { value: 'aura-athena-en', label: 'Athena (Female)' },
    { value: 'aura-hera-en', label: 'Hera (Female)' },
    { value: 'aura-orion-en', label: 'Orion (Male)' },
    { value: 'aura-arcas-en', label: 'Arcas (Male)' },
    { value: 'aura-perseus-en', label: 'Perseus (Male)' },
    { value: 'aura-angus-en', label: 'Angus (Irish Male)' },
    { value: 'aura-orpheus-en', label: 'Orpheus (Male)' },
    { value: 'aura-helios-en', label: 'Helios (British Male)' },
    { value: 'aura-zeus-en', label: 'Zeus (Male)' }
  ]},
  openai: { name: 'OpenAI', voices: [
    { value: 'alloy', label: 'Alloy (Neutral)' },
    { value: 'echo', label: 'Echo (Male)' },
    { value: 'fable', label: 'Fable (British)' },
    { value: 'onyx', label: 'Onyx (Deep Male)' },
    { value: 'nova', label: 'Nova (Female)' },
    { value: 'shimmer', label: 'Shimmer (Female)' }
  ]},
  azure: { name: 'Azure', voices: [
    { value: 'en-US-JennyNeural', label: 'Jenny (US)' },
    { value: 'en-US-GuyNeural', label: 'Guy (US)' },
    { value: 'en-GB-SoniaNeural', label: 'Sonia (UK)' },
    { value: 'en-GB-RyanNeural', label: 'Ryan (UK)' },
    { value: 'ur-PK-UzmaNeural', label: 'Uzma (Urdu)' },
    { value: 'ur-PK-AsadNeural', label: 'Asad (Urdu)' },
    { value: 'hi-IN-SwaraNeural', label: 'Swara (Hindi)' },
    { value: 'hi-IN-MadhurNeural', label: 'Madhur (Hindi)' },
    { value: 'ar-SA-ZariyahNeural', label: 'Zariyah (Arabic)' },
    { value: 'ar-SA-HamedNeural', label: 'Hamed (Arabic)' }
  ]}
};

const INTENT_TYPES = [
  { value: 'static', label: 'Static Response', icon: MessageSquare, desc: 'Play fixed response' },
  { value: 'function_call', label: 'Function Call', icon: Zap, desc: 'Call an API' },
  { value: 'flow', label: 'Start Flow', icon: GitBranch, desc: 'Multi-turn conversation' },
  { value: 'script', label: 'Start Script', icon: ClipboardList, desc: 'Structured survey/script' },
  { value: 'transfer', label: 'Transfer', icon: Phone, desc: 'Transfer to agent' }
];

export default function IVRConfig() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  
  const [agent, setAgent] = useState(null);
  const [config, setConfig] = useState(null);
  const [intents, setIntents] = useState([]);
  const [flows, setFlows] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('flow');
  const [showIntentModal, setShowIntentModal] = useState(false);
  const [editingIntent, setEditingIntent] = useState(null);
  const [classifierProviders, setClassifierProviders] = useState(FALLBACK_CLASSIFIER_PROVIDERS);

  useEffect(() => { loadData(); }, [agentId]);

  // Classifier models come from the shared LLM catalog so a decommissioned
  // model disappears from this dropdown without a dashboard release.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/agents/llm-models');
        if (cancelled || !data?.models?.length) return;
        setClassifierProviders(buildClassifierProviders(data.models));
      } catch (err) {
        console.warn('Could not load LLM catalog, using bundled fallback', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [agentRes, configRes, intentsRes, flowsRes] = await Promise.all([
        api.get(`/agents/${agentId}`),
        api.get(`/ivr/${agentId}/config`),
        api.get(`/ivr/${agentId}/intents`),
        api.get(`/flows/${agentId}`)
      ]);
      
      setAgent(agentRes.data.agent || agentRes.data);
      setConfig(configRes.data.data);
      setIntents(intentsRes.data.data || []);
      setFlows(flowsRes.data.data || []);
      
      try {
        const funcRes = await api.get(`/functions/agent/${agentId}`);
        setFunctions(funcRes.data.functions || []);
      } catch (e) { setFunctions([]); }
      
      try {
        const audioRes = await api.get(`/ivr/${agentId}/audio`);
        setAudioFiles(audioRes.data.data || []);
      } catch (e) { setAudioFiles([]); }
      
      try {
        const scriptsRes = await api.get(`/scripts/${agentId}`);
        setScripts(scriptsRes.data.data || []);
      } catch (e) { setScripts([]); }
      
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      await api.put(`/ivr/${agentId}/config`, config);
      toast.success('Configuration saved');
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleSaveIntent = async () => {
    if (!editingIntent.name) { toast.error('Name required'); return; }
    try {
      if (editingIntent.id) {
        await api.put(`/ivr/${agentId}/intents/${editingIntent.id}`, editingIntent);
      } else {
        await api.post(`/ivr/${agentId}/intents`, editingIntent);
      }
      toast.success('Intent saved');
      setShowIntentModal(false);
      setEditingIntent(null);
      loadData();
    } catch (error) {
      toast.error('Failed to save intent');
    }
  };

  const handleDeleteIntent = async (id) => {
    if (!confirm('Delete this intent?')) return;
    try {
      await api.delete(`/ivr/${agentId}/intents/${id}`);
      toast.success('Deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-blue-500" /></div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/agents')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Intent IVR Configuration</h1>
            <p className="text-gray-600">{agent?.name}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => navigate(`/agents/${agentId}/audio`)} className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50">
            <Volume2 className="w-4 h-4" /> Audio Library
          </button>
          <button onClick={() => navigate(`/agents/${agentId}/flows`)} className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50">
            <GitBranch className="w-4 h-4" /> Flows
          </button>
          <button onClick={() => navigate(`/agents/${agentId}/scripts`)} className="flex items-center gap-2 px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50">
            <ClipboardList className="w-4 h-4" /> Outbound Scripts
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { id: 'flow', label: 'Call Flow', icon: PhoneIncoming },
          { id: 'providers', label: 'AI Providers', icon: Brain },
          { id: 'intents', label: 'Intents', icon: MessageSquare }
        ].map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${activeSection === s.id ? 'bg-blue-600 text-white' : 'bg-white border hover:bg-gray-50'}`}>
            <s.icon className="w-4 h-4" /> {s.label}
          </button>
        ))}
      </div>

      {/* Call Flow Section */}
      {activeSection === 'flow' && config && (
        <div className="space-y-6">
          {/* Flow Diagram */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4">Call Flow</h2>
            <div className="flex items-center justify-between overflow-x-auto pb-4 gap-2">
              {[
                { icon: PhoneIncoming, label: 'Call Starts', color: 'green' },
                { icon: Volume2, label: 'Welcome', color: 'blue' },
                { icon: Mic, label: 'Listen', color: 'purple' },
                { icon: Brain, label: 'Match Intent', color: 'orange' },
                { icon: MessageSquare, label: 'Respond', color: 'teal' },
                { icon: PhoneOff, label: 'End/Loop', color: 'red' }
              ].map((step, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                  <div className="flex flex-col items-center min-w-[100px]">
                    <div className={`w-12 h-12 bg-${step.color}-100 rounded-full flex items-center justify-center mb-2`}>
                      <step.icon className={`w-6 h-6 text-${step.color}-600`} />
                    </div>
                    <span className="text-sm font-medium">{step.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Messages */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <PhoneIncoming className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Welcome Message</h3>
                  <p className="text-sm text-gray-500">Played when call starts</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Audio File</label>
                  <select value={config.welcome_audio_id || ''} onChange={(e) => setConfig({...config, welcome_audio_id: e.target.value || null})} className="w-full border rounded-lg px-3 py-2">
                    <option value="">Use text below</option>
                    {audioFiles.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Welcome Text</label>
                  <textarea value={config.welcome_message || ''} onChange={(e) => setConfig({...config, welcome_message: e.target.value})} className="w-full border rounded-lg px-3 py-2" rows={3} placeholder="Welcome! How can I help?" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <PhoneOff className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Goodbye Message</h3>
                  <p className="text-sm text-gray-500">Played when call ends</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Audio File</label>
                  <select value={config.goodbye_audio_id || ''} onChange={(e) => setConfig({...config, goodbye_audio_id: e.target.value || null})} className="w-full border rounded-lg px-3 py-2">
                    <option value="">Use text below</option>
                    {audioFiles.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Goodbye Text</label>
                  <textarea value={config.goodbye_message || ''} onChange={(e) => setConfig({...config, goodbye_message: e.target.value})} className="w-full border rounded-lg px-3 py-2" rows={3} placeholder="Thank you for calling. Goodbye!" />
                </div>
              </div>
            </div>
          </div>

          {/* Fallback */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold">Fallback Handling</h3>
                <p className="text-sm text-gray-500">When intent not understood</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Max Retries</label>
                <input type="number" value={config.max_fallback_count || 3} onChange={(e) => setConfig({...config, max_fallback_count: parseInt(e.target.value)})} className="w-full border rounded-lg px-3 py-2" min="1" max="10" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">After Max Retries</label>
                <select value={config.fallback_action || 'transfer'} onChange={(e) => setConfig({...config, fallback_action: e.target.value})} className="w-full border rounded-lg px-3 py-2">
                  <option value="transfer">Transfer to Agent</option>
                  <option value="repeat">Repeat Welcome</option>
                  <option value="end">End Call</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Transfer Queue</label>
                <input type="text" value={config.transfer_queue || ''} onChange={(e) => setConfig({...config, transfer_queue: e.target.value})} className="w-full border rounded-lg px-3 py-2" placeholder="support" />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium mb-1">Not Understood Message</label>
                <textarea value={config.not_found_message || ''} onChange={(e) => setConfig({...config, not_found_message: e.target.value})} className="w-full border rounded-lg px-3 py-2" rows={2} placeholder="Sorry, I didn't understand..." />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium mb-1">Transfer Message</label>
                <textarea value={config.transfer_message || ''} onChange={(e) => setConfig({...config, transfer_message: e.target.value})} className="w-full border rounded-lg px-3 py-2" rows={2} placeholder="Please hold..." />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={saveConfig} className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Save className="w-4 h-4" /> Save Call Flow
            </button>
          </div>
        </div>
      )}

      {/* Providers Section */}
      {activeSection === 'providers' && config && (
        <div className="space-y-6">
          {/* STT */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Mic className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold">Speech-to-Text (STT)</h3>
                <p className="text-sm text-gray-500">Converts speech to text</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Provider</label>
                <select value={config.stt_provider || 'soniox'} onChange={(e) => setConfig({...config, stt_provider: e.target.value, stt_model: STT_PROVIDERS[e.target.value]?.models[0]?.value})} className="w-full border rounded-lg px-3 py-2">
                  {Object.entries(STT_PROVIDERS).map(([k,v]) => <option key={k} value={k}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Model</label>
                <select value={config.stt_model || ''} onChange={(e) => setConfig({...config, stt_model: e.target.value})} className="w-full border rounded-lg px-3 py-2">
                  {STT_PROVIDERS[config.stt_provider || 'soniox']?.models.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Language</label>
                <select value={config.stt_language || 'en'} onChange={(e) => setConfig({...config, stt_language: e.target.value})} className="w-full border rounded-lg px-3 py-2">
                  <option value="en">English</option>
                  <option value="ur">Urdu</option>
                  <option value="hi">Hindi</option>
                  <option value="ar">Arabic</option>
                  <option value="multi">Multi</option>
                </select>
              </div>
            </div>
          </div>

          {/* Classifier */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold">Intent Classifier (LLM)</h3>
                <p className="text-sm text-gray-500">Matches speech to intents</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Provider</label>
                <select value={config.classifier_provider || 'groq'} onChange={(e) => setConfig({...config, classifier_provider: e.target.value, classifier_model: classifierProviders[e.target.value]?.models[0]?.value})} className="w-full border rounded-lg px-3 py-2">
                  {Object.entries(classifierProviders).map(([k,v]) => <option key={k} value={k}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Model</label>
                <select value={config.classifier_model || ''} onChange={(e) => setConfig({...config, classifier_model: e.target.value})} className="w-full border rounded-lg px-3 py-2">
                  {classifierProviders[config.classifier_provider || 'groq']?.models.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  {/* Keep a saved-but-no-longer-offered model selectable so opening
                      this page doesn't silently rewrite the IVR's classifier. */}
                  {config.classifier_model &&
                   !classifierProviders[config.classifier_provider || 'groq']?.models.some(m => m.value === config.classifier_model) && (
                    <option value={config.classifier_model}>{config.classifier_model} (discontinued)</option>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Temperature</label>
                <input type="number" value={config.classifier_temperature || 0} onChange={(e) => setConfig({...config, classifier_temperature: parseFloat(e.target.value)})} className="w-full border rounded-lg px-3 py-2" min="0" max="1" step="0.1" />
              </div>
            </div>
          </div>

          {/* TTS */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <Volume2 className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold">Text-to-Speech (TTS)</h3>
                <p className="text-sm text-gray-500">Speaks responses</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Provider</label>
                <select value={config.tts_provider || 'elevenlabs'} onChange={(e) => setConfig({...config, tts_provider: e.target.value, tts_voice: TTS_PROVIDERS[e.target.value]?.voices[0]?.value})} className="w-full border rounded-lg px-3 py-2">
                  {Object.entries(TTS_PROVIDERS).map(([k,v]) => <option key={k} value={k}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Voice</label>
                <select value={config.tts_voice || ''} onChange={(e) => setConfig({...config, tts_voice: e.target.value})} className="w-full border rounded-lg px-3 py-2">
                  {TTS_PROVIDERS[config.tts_provider || 'elevenlabs']?.voices.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Speed</label>
                <input type="number" value={config.tts_speed || 1.0} onChange={(e) => setConfig({...config, tts_speed: parseFloat(e.target.value)})} className="w-full border rounded-lg px-3 py-2" min="0.5" max="2.0" step="0.1" />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={saveConfig} className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Save className="w-4 h-4" /> Save Providers
            </button>
          </div>
        </div>
      )}

      {/* Intents Section */}
      {activeSection === 'intents' && (
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-4 border-b flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Intents</h2>
              <p className="text-sm text-gray-500">Define what callers can ask for</p>
            </div>
            <button onClick={() => { setEditingIntent({ name: '', intent_key: '', description: '', trigger_phrases: [], intent_type: 'static', response_text: '', priority: 0, is_active: true }); setShowIntentModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Plus className="w-4 h-4" /> Add Intent
            </button>
          </div>

          {intents.length === 0 ? (
            <div className="p-12 text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No intents yet</h3>
              <p className="text-gray-500 mb-4">Create intents to define caller options</p>
            </div>
          ) : (
            <div className="divide-y">
              {intents.map((intent) => (
                <div key={intent.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <button onClick={async () => { await api.put(`/ivr/${agentId}/intents/${intent.id}`, { is_active: !intent.is_active }); loadData(); }}
                        className={`mt-1 w-10 h-6 rounded-full ${intent.is_active ? 'bg-green-500' : 'bg-gray-300'}`}>
                        <div className={`w-4 h-4 bg-white rounded-full shadow ${intent.is_active ? 'translate-x-5' : 'translate-x-1'}`} />
                      </button>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium">{intent.name}</h3>
                          <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{intent.intent_key}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${intent.intent_type === 'static' ? 'bg-blue-100 text-blue-700' : intent.intent_type === 'function_call' ? 'bg-purple-100 text-purple-700' : intent.intent_type === 'flow' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                            {intent.intent_type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{intent.description}</p>
                        {intent.trigger_phrases?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {intent.trigger_phrases.slice(0, 5).map((p, i) => <span key={i} className="text-xs px-2 py-0.5 bg-gray-50 border rounded">{p}</span>)}
                            {intent.trigger_phrases.length > 5 && <span className="text-xs text-gray-500">+{intent.trigger_phrases.length - 5}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setEditingIntent({...intent}); setShowIntentModal(true); }} className="p-2 hover:bg-gray-100 rounded-lg"><Edit2 className="w-4 h-4 text-gray-500" /></button>
                      <button onClick={() => handleDeleteIntent(intent.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Intent Modal */}
      {showIntentModal && editingIntent && (
        <IntentModal intent={editingIntent} onChange={setEditingIntent} onClose={() => { setShowIntentModal(false); setEditingIntent(null); }} onSave={handleSaveIntent} functions={functions} flows={flows} scripts={scripts} audioFiles={audioFiles} />
      )}
    </div>
  );
}

function IntentModal({ intent, onChange, onClose, onSave, functions, flows, scripts, audioFiles }) {
  const [triggerInput, setTriggerInput] = useState('');
  const addPhrase = () => { if (triggerInput.trim()) { onChange({...intent, trigger_phrases: [...(intent.trigger_phrases || []), triggerInput.trim()]}); setTriggerInput(''); }};
  const removePhrase = (i) => onChange({...intent, trigger_phrases: intent.trigger_phrases.filter((_, idx) => idx !== i)});

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold">{intent.id ? 'Edit Intent' : 'Create Intent'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input type="text" value={intent.name} onChange={(e) => onChange({...intent, name: e.target.value, intent_key: intent.intent_key || e.target.value.toLowerCase().replace(/\s+/g, '_')})} className="w-full border rounded-lg px-3 py-2" placeholder="Check Balance" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Key</label>
              <input type="text" value={intent.intent_key} onChange={(e) => onChange({...intent, intent_key: e.target.value})} className="w-full border rounded-lg px-3 py-2" placeholder="check_balance" />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea value={intent.description || ''} onChange={(e) => onChange({...intent, description: e.target.value})} className="w-full border rounded-lg px-3 py-2" rows={2} />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Trigger Phrases</label>
            <div className="flex gap-2 mb-2">
              <input type="text" value={triggerInput} onChange={(e) => setTriggerInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPhrase())} className="flex-1 border rounded-lg px-3 py-2" placeholder="Enter phrase..." />
              <button onClick={addPhrase} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(intent.trigger_phrases || []).map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                  {p}<button onClick={() => removePhrase(i)}>×</button>
                </span>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Action Type</label>
            <div className="grid grid-cols-2 gap-2">
              {INTENT_TYPES.map((t) => (
                <button key={t.value} type="button" onClick={() => onChange({...intent, intent_type: t.value})}
                  className={`p-3 border rounded-lg text-left flex items-start gap-3 ${intent.intent_type === t.value ? 'border-blue-500 bg-blue-50' : 'hover:border-gray-300'}`}>
                  <t.icon className="w-5 h-5 mt-0.5 text-gray-600" />
                  <div><div className="font-medium text-sm">{t.label}</div><div className="text-xs text-gray-500">{t.desc}</div></div>
                </button>
              ))}
            </div>
          </div>
          
          {intent.intent_type === 'static' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium mb-1">Audio</label>
                <select value={intent.response_audio_id || ''} onChange={(e) => onChange({...intent, response_audio_id: e.target.value || null})} className="w-full border rounded-lg px-3 py-2">
                  <option value="">Use text</option>
                  {audioFiles.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Response Text</label>
                <textarea value={intent.response_text || ''} onChange={(e) => onChange({...intent, response_text: e.target.value})} className="w-full border rounded-lg px-3 py-2" rows={3} />
              </div>
            </div>
          )}
          
          {intent.intent_type === 'function_call' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium mb-1">Function</label>
                <select value={intent.function_name || ''} onChange={(e) => onChange({...intent, function_name: e.target.value})} className="w-full border rounded-lg px-3 py-2">
                  <option value="">Select...</option>
                  {functions.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Acknowledgment</label>
                <textarea value={intent.response_text || ''} onChange={(e) => onChange({...intent, response_text: e.target.value})} className="w-full border rounded-lg px-3 py-2" rows={2} placeholder="Let me check..." />
              </div>
            </div>
          )}
          
          {intent.intent_type === 'flow' && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium mb-1">Flow</label>
              <select value={intent.flow_id || ''} onChange={(e) => onChange({...intent, flow_id: e.target.value})} className="w-full border rounded-lg px-3 py-2">
                <option value="">Select...</option>
                {flows.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          )}
          
          {intent.intent_type === 'script' && (
            <div className="space-y-3 p-4 bg-purple-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium mb-1">Script</label>
                <select value={intent.script_id || ''} onChange={(e) => onChange({...intent, script_id: e.target.value})} className="w-full border rounded-lg px-3 py-2">
                  <option value="">Select a script...</option>
                  {scripts.map(s => <option key={s.id} value={s.id}>{s.name} ({s.script_type})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Introduction Message (optional)</label>
                <textarea value={intent.response_text || ''} onChange={(e) => onChange({...intent, response_text: e.target.value})} className="w-full border rounded-lg px-3 py-2" rows={2} placeholder="Let me start the survey..." />
              </div>
              {scripts.length === 0 && (
                <p className="text-sm text-gray-500">No scripts available. Create scripts in the Outbound Scripts section.</p>
              )}
            </div>
          )}
          
          {intent.intent_type === 'transfer' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium mb-1">Queue</label>
                <input type="text" value={intent.transfer_queue || ''} onChange={(e) => onChange({...intent, transfer_queue: e.target.value})} className="w-full border rounded-lg px-3 py-2" placeholder="support" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Message</label>
                <textarea value={intent.response_text || ''} onChange={(e) => onChange({...intent, response_text: e.target.value})} className="w-full border rounded-lg px-3 py-2" rows={2} placeholder="Transferring..." />
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-1">Priority</label>
            <input type="number" value={intent.priority || 0} onChange={(e) => onChange({...intent, priority: parseInt(e.target.value)})} className="w-full border rounded-lg px-3 py-2" min="0" max="100" />
          </div>
        </div>
        
        <div className="p-6 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={onSave} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{intent.id ? 'Save' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}